import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { capitalize, computed, onMounted, ref, watch } from 'vue'
import { resolveCalendarPresetRange, useDateTime, type CalendarPresetId } from '@ai/ui-components'
import type {
  ColdMonitoringResponseCall,
  ColdMonitoringPagination,
  ColdMonitoringResponse,
  DisconnectedBy,
  VaLanguage,
  ColdMonitoringSortKey,
  ColdMonitoringSortDirection,
  ColdMonitoringQuery,
  ColdMonitoringKPI,
  ChartInterval,
} from 'shared'
import type { PeriodRange } from '@/types/Common'
import type { ChartProps } from '@/types/Charts'
import { useColdMonitoringApi } from '@/composables/api/useColdMonitoringApi'
import { currentTZDateAndTimeToTimestamp } from '@/utils/datetime'
import {
  formatChartCategories,
  getCurrentHighlightIndex,
  localizeChartUnits,
} from '@/utils/chart-labels'
import { useI18n } from 'vue-i18n'

export const DEFAULT_PAGE_SIZE = 25
const STORAGE_KEY = STORAGE_KEYS.COLD_MONITORING_FILTERS
const CACHE_TTL = 30000 // 30 seconds
const AVAILABLE_INTERVALS: ChartInterval[] = ['hour', 'day', 'week', 'month', 'quarter', 'year']
const CHART_FORMAT_BY_INTERVAL = {
  '15m': 'chartHourTick',
  hour: 'chartHourTick',
  day: 'chartDayTick',
  week: 'chartWeekTick',
  month: 'chartMonthTick',
  quarter: 'chartQuarterTick',
  year: 'chartYearTick',
} as const
const DISCONNECTED_BY_VALUES_ALLOWED_WITHOUT_BACKEND_FILTERS: DisconnectedBy[] = ['other']
const RELATIVE_PERIOD_PRESET_IDS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_week',
  'last_30_days',
] as const satisfies readonly CalendarPresetId[]

export type ColdMonitoringRelativePeriodPresetId = (typeof RELATIVE_PERIOD_PRESET_IDS)[number]

export interface ColdMonitoringSort {
  by: ColdMonitoringSortKey
  direction: ColdMonitoringSortDirection
}

export interface ColdMonitoringFiltersState {
  period: PeriodRange | null
  periodPresetId: ColdMonitoringRelativePeriodPresetId | null
  agents: string[]
  languages: VaLanguage[]
  disconnectedBy: DisconnectedBy[]
  showLiveTests: boolean
}

interface ColdMonitoringPaginationState {
  currentPage: number
  itemsPerPage: number
  totalItems: number
  hasMore?: boolean
}

interface ColdMonitoringStatusState {
  isLoading: boolean
  error: string | null
}

interface ColdMonitoringState {
  calls: ColdMonitoringResponseCall[]
  filters: ColdMonitoringFiltersState
  pagination: ColdMonitoringPaginationState
  sort: ColdMonitoringSort
  availableFilters: ColdMonitoringResponse['filters']
  status: ColdMonitoringStatusState
  // New fields for filter tracking
  isDefaultFilters: boolean
  hasUserInteracted: boolean
}

interface ColdMonitoringPreferences {
  filters: ColdMonitoringFiltersState
  sort: ColdMonitoringSort
  itemsPerPage?: number
  isDefaultFilters?: boolean
  hasUserInteracted?: boolean
}

interface CachedResponse {
  calls: ColdMonitoringResponseCall[]
  stats: ColdMonitoringKPI[]
  availableFilters: ColdMonitoringResponse['filters']
  pagination: ColdMonitoringPaginationState
  timestamp: number
}

type StoredPeriodFilter = Partial<Pick<ColdMonitoringFiltersState, 'period' | 'periodPresetId'>>

const isRelativePeriodPresetId = (value: unknown): value is ColdMonitoringRelativePeriodPresetId =>
  typeof value === 'string' &&
  RELATIVE_PERIOD_PRESET_IDS.includes(value as ColdMonitoringRelativePeriodPresetId)

const normalizeStoredPeriodFilter = (
  storedFilter?: StoredPeriodFilter,
): Pick<ColdMonitoringFiltersState, 'period' | 'periodPresetId'> => {
  if (!storedFilter) {
    return {
      period: null,
      periodPresetId: null,
    }
  }

  if (isRelativePeriodPresetId(storedFilter.periodPresetId)) {
    return {
      period: resolveCalendarPresetRange(storedFilter.periodPresetId),
      periodPresetId: storedFilter.periodPresetId,
    }
  }

  return {
    period: storedFilter.period ?? null,
    periodPresetId: null,
  }
}

const emptyPeriod = (): PeriodRange => ({
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
})

const clonePeriod = (range: PeriodRange | null | undefined): PeriodRange => ({
  startDate: range?.startDate ?? null,
  endDate: range?.endDate ?? null,
  startTime: range?.startTime ?? null,
  endTime: range?.endTime ?? null,
})

const toRelativePeriodPresetId = (
  presetId: CalendarPresetId | null,
): ColdMonitoringRelativePeriodPresetId | null =>
  isRelativePeriodPresetId(presetId) ? presetId : null

const isPeriodEmpty = (period: PeriodRange | null | undefined) =>
  !period?.startDate && !period?.endDate && !period?.startTime && !period?.endTime

// When most agents are selected it's more efficient to send the excluded IDs with all_but=true
// rather than all selected IDs. This avoids 422 errors when agent count exceeds backend limits.
// Caveat: the __deleted__ sentinel counts as 1 here but expands to N real IDs on the BFF.
// In orgs with many deleted agents the excluded list may exceed the backend limit even when
// this heuristic picks allBut. Fixing requires the BFF to expose the deleted-agent count.
const resolveAgentFilter = (
  selected: string[],
  available: { id: string }[],
): { agents: string[]; allBut: boolean } => {
  if (!available.length || !selected.length) return { agents: selected, allBut: false }
  const selectedSet = new Set(selected)
  const excluded = available.map((a) => a.id).filter((id) => !selectedSet.has(id))
  if (excluded.length === 0) return { agents: [], allBut: false }
  if (excluded.length < selected.length) return { agents: excluded, allBut: true }
  return { agents: selected, allBut: false }
}

const normalizeCalendarRangeForCommit = (
  period: PeriodRange,
  presetId: ColdMonitoringRelativePeriodPresetId | null,
): PeriodRange | null => (presetId === null && isPeriodEmpty(period) ? null : clonePeriod(period))

const createDefaultFilters = (): ColdMonitoringFiltersState => ({
  period: null,
  periodPresetId: null,
  agents: [],
  languages: [],
  disconnectedBy: [],
  showLiveTests: false,
})

const createDefaultState = (): ColdMonitoringState => ({
  calls: [],
  filters: createDefaultFilters(),
  pagination: {
    currentPage: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    totalItems: 0,
    hasMore: false,
  },
  sort: {
    by: 'startTime',
    direction: 'desc',
  },
  availableFilters: {
    agents: [],
    languages: [],
    disconnectedBy: [],
  },
  status: {
    isLoading: false,
    error: null,
  },
  isDefaultFilters: true,
  hasUserInteracted: false,
})

export const useColdMonitoringCoordinator = () => {
  const { t } = useI18n({ useScope: 'global' })
  const { formatDateTime, getChartTooltipFormat } = useDateTime()

  const normalizeChartCategoryLabel = (label: unknown): string => {
    if (label == null) {
      return t('empty')
    }

    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const normalizedLabel = String(label).trim()
    return normalizedLabel.length > 0 ? normalizedLabel : t('empty')
  }

  // Main state
  const state = ref<ColdMonitoringState>(createDefaultState())

  // Stats state
  const stats = ref<ColdMonitoringKPI[]>([])
  const isLoadingStats = ref(false)
  const statsError = ref<string | null>(null)

  // Chart state
  const isModalOpen = ref(false)
  const selectedKpi = ref<ColdMonitoringKPI | null>(null)
  const chartInterval = ref<ChartInterval>('day')
  const chartData = ref<ChartProps>({
    chartType: 'bar',
    series: [],
    colors: ['#6657EB', '#10B981', '#F59E0B', '#4F46E5'],
    loading: false,
  })

  const chartView = ref<'bar' | 'pie'>('pie')
  const outputDistributionView = ref<'bar' | 'pie'>('pie')

  const isLoadingChart = ref(false)
  const chartError = ref<string | null>(null)
  const rawCategories = ref<string[] | undefined>(undefined)

  // Cache
  const cachedResults = new Map<string, CachedResponse>()

  // API
  const { fetchColdCalls, fetchColdStats, fetchColdChart } = useColdMonitoringApi({
    period: ref(undefined),
    agents: ref(undefined),
  })

  // ========== CACHE MANAGEMENT ==========

  const generateCacheKey = (filters: ColdMonitoringFiltersState): string => {
    return JSON.stringify({
      agents: [...filters.agents].sort(),
      languages: [...filters.languages].sort(),
      disconnectedBy: [...filters.disconnectedBy].sort(),
      period: filters.period,
      showLiveTests: filters.showLiveTests,
      page: state.value.pagination.currentPage,
      pageSize: state.value.pagination.itemsPerPage,
      sortBy: state.value.sort.by,
      sortDirection: state.value.sort.direction,
    })
  }

  // ========== STORAGE MANAGEMENT ==========

  const persistPreferences = () => {
    const payload: ColdMonitoringPreferences = {
      filters: state.value.filters,
      sort: state.value.sort,
      itemsPerPage: state.value.pagination.itemsPerPage,
      isDefaultFilters: state.value.isDefaultFilters,
      hasUserInteracted: state.value.hasUserInteracted,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }

  const loadPreferences = () => {
    const saved = localStorage.getItem(STORAGE_KEY)

    if (!saved) {
      // First visit - use defaults
      state.value.isDefaultFilters = true
      state.value.hasUserInteracted = false
      return
    }

    try {
      const parsed = JSON.parse(saved) as Partial<ColdMonitoringPreferences>

      if (parsed.filters) {
        const normalizedPeriodFilter = normalizeStoredPeriodFilter(parsed.filters)

        state.value.filters = {
          ...state.value.filters,
          ...parsed.filters,
          ...normalizedPeriodFilter,
        }
      }
      if (parsed.sort) state.value.sort = parsed.sort
      if (parsed.itemsPerPage) state.value.pagination.itemsPerPage = parsed.itemsPerPage

      // Backward compatibility with old format
      state.value.isDefaultFilters = parsed.isDefaultFilters ?? false
      state.value.hasUserInteracted = parsed.hasUserInteracted ?? true
    } catch (err) {
      console.warn('Failed to parse cold monitoring preferences', err)
      state.value.isDefaultFilters = true
      state.value.hasUserInteracted = false
    }
  }

  // ========== FILTER MANAGEMENT ==========

  const ensureFiltersWithinAvailable = (skipWatcher = false) => {
    const available = state.value.availableFilters
    const filters = state.value.filters

    const availableAgentIds = available.agents.map((agent) => agent.id)

    // Skip watcher trigger if requested (e.g., during initial load)
    if (skipWatcher) {
      skipNextRefresh.value = true
    }

    // Keep "nothing selected" as a valid state; only clean invalid values.
    filters.agents = filters.agents.filter((id) => availableAgentIds.includes(id))
    filters.languages = filters.languages.filter((lang) => available.languages.includes(lang))
    filters.disconnectedBy = filters.disconnectedBy.filter(
      (value) =>
        available.disconnectedBy.includes(value) ||
        DISCONNECTED_BY_VALUES_ALLOWED_WITHOUT_BACKEND_FILTERS.includes(value),
    )
  }

  // ========== API QUERY BUILDING ==========

  const mapPagination = (pagination: ColdMonitoringPagination): ColdMonitoringPaginationState => ({
    currentPage: pagination.page,
    itemsPerPage: pagination.pageSize,
    totalItems: pagination.total,
    hasMore: pagination.page < pagination.totalPages,
  })

  const buildApiFilters = (): ColdMonitoringQuery => {
    const { filters, pagination, sort } = state.value
    const period = filters.period
    const datetimeFrom = currentTZDateAndTimeToTimestamp(period?.startDate, period?.startTime)
    const datetimeTo = currentTZDateAndTimeToTimestamp(period?.endDate, period?.endTime)
    const { agents, allBut } = resolveAgentFilter(
      filters.agents,
      state.value.availableFilters.agents,
    )

    return {
      datetimeFrom: datetimeFrom,
      datetimeTo: datetimeTo,
      agents,
      allBut: allBut || undefined,
      languages: filters.languages,
      disconnectedBy: filters.disconnectedBy,
      includeLiveTests: filters.showLiveTests,
      page: pagination.currentPage,
      pageSize: pagination.itemsPerPage,
      sortBy: sort.by,
      sortDirection: sort.direction,
    }
  }

  const buildStatsQuery = () => {
    const { filters } = state.value
    const datetimeFrom = currentTZDateAndTimeToTimestamp(
      filters.period?.startDate,
      filters.period?.startTime,
    )
    const datetimeTo = currentTZDateAndTimeToTimestamp(
      filters.period?.endDate,
      filters.period?.endTime,
    )
    const { agents, allBut } = resolveAgentFilter(
      filters.agents,
      state.value.availableFilters.agents,
    )

    return {
      datetimeFrom,
      datetimeTo,
      agents,
      allBut: allBut || undefined,
      languages: [...filters.languages],
      disconnectedBy: [...filters.disconnectedBy],
      includeLiveTests: filters.showLiveTests,
    }
  }

  // ========== COORDINATED REFRESH ==========

  const refresh = async (options?: { force?: boolean }) => {
    const cacheKey = generateCacheKey(state.value.filters)
    const requestedPageSize = state.value.pagination.itemsPerPage

    // Check cache (unless force refresh)
    if (!options?.force && cachedResults.has(cacheKey)) {
      const cached = cachedResults.get(cacheKey)
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        // Restore from cache
        state.value.calls = cached.calls
        stats.value = cached.stats
        state.value.availableFilters = cached.availableFilters
        state.value.pagination = { ...cached.pagination, itemsPerPage: requestedPageSize }
        return
      }
    }

    // Fetch data (table + stats in parallel)
    state.value.status.isLoading = true
    state.value.status.error = null
    isLoadingStats.value = true
    statsError.value = null

    try {
      const [callsResponse, statsResponse] = await Promise.all([
        fetchColdCalls(buildApiFilters()),
        fetchColdStats(buildStatsQuery()),
      ])

      // Update state
      state.value.calls = callsResponse.calls
      state.value.availableFilters = callsResponse.filters
      ensureFiltersWithinAvailable(true) // Skip watcher to avoid duplicate refresh
      state.value.pagination = {
        ...mapPagination(callsResponse.pagination),
        itemsPerPage: requestedPageSize,
      }

      stats.value = statsResponse.kpis

      // Cache results (pagination already has requestedPageSize applied)
      cachedResults.set(cacheKey, {
        calls: callsResponse.calls,
        stats: statsResponse.kpis,
        availableFilters: callsResponse.filters,
        pagination: { ...state.value.pagination },
        timestamp: Date.now(),
      })

      // Don't persist here - only persist on user actions (applyFilters, setSort, etc.)
    } catch (err) {
      console.error(err)
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      state.value.status.error = errorMessage
      statsError.value = errorMessage
    } finally {
      state.value.status.isLoading = false
      isLoadingStats.value = false
    }
  }

  // ========== FILTER ACTIONS ==========

  const applyFilters = (filters: Partial<ColdMonitoringFiltersState>) => {
    state.value.filters = {
      ...state.value.filters,
      ...filters,
    }
    state.value.pagination.currentPage = 1

    // Don't persist or mark as interaction if not initialized yet
    if (!isInitialized.value) {
      return Promise.resolve()
    }

    // Mark user interaction
    state.value.hasUserInteracted = true
    state.value.isDefaultFilters = false

    persistPreferences()

    // Don't call refresh() here - let the watcher handle it
    // This prevents multiple refresh calls when the component calls applyFilters multiple times
    return Promise.resolve()
  }

  const setPeriodFilter = (
    period: PeriodRange | null,
    periodPresetId: ColdMonitoringRelativePeriodPresetId | null,
  ) => applyFilters({ period, periodPresetId })

  const calendarRange = ref<PeriodRange>(emptyPeriod())
  const calendarPresetId = ref<CalendarPresetId | null>(null)

  const setCalendarRange = (range: PeriodRange) => {
    calendarRange.value = clonePeriod(range)
  }

  const setCalendarPresetId = (presetId: CalendarPresetId | null) => {
    calendarPresetId.value = toRelativePeriodPresetId(presetId)
  }

  const applyCalendarFilter = () => {
    const presetId = toRelativePeriodPresetId(calendarPresetId.value)
    return setPeriodFilter(normalizeCalendarRangeForCommit(calendarRange.value, presetId), presetId)
  }

  const clearCalendarFilter = () => {
    calendarRange.value = emptyPeriod()
    calendarPresetId.value = null
    return setPeriodFilter(null, null)
  }

  const setPage = (page: number) => {
    state.value.pagination = {
      ...state.value.pagination,
      currentPage: page,
    }
    return refresh()
  }

  const setPageSize = (size: number) => {
    state.value.pagination = {
      ...state.value.pagination,
      itemsPerPage: size,
      currentPage: 1,
    }
    persistPreferences()
    return refresh()
  }

  const setSort = (by: ColdMonitoringSortKey, direction: ColdMonitoringSortDirection) => {
    state.value.sort = { by, direction }
    state.value.pagination.currentPage = 1
    persistPreferences()
    return refresh()
  }

  const resetFilters = () => {
    state.value.filters = createDefaultFilters()
    state.value.pagination.currentPage = 1
    state.value.isDefaultFilters = true
    state.value.hasUserInteracted = false
    persistPreferences()
    return refresh()
  }

  // ========== CHART MANAGEMENT ==========

  const clearChartData = () => {
    chartData.value = {
      chartType: 'bar',
      series: [],
      colors: ['#6657EB', '#10B981', '#F59E0B', '#4F46E5'],
      loading: false,
    }
    rawCategories.value = undefined
  }

  const localPeriod = ref<PeriodRange>(clonePeriod(state.value.filters.period))

  const chartPeriod = computed<PeriodRange>({
    get: () => localPeriod.value,
    set: (value: PeriodRange) => {
      if (value.startDate === '' && value.endDate === '') return
      localPeriod.value = clonePeriod(value)
    },
  })

  const loadChart = async () => {
    if (!isModalOpen.value || !selectedKpi.value) return
    if (chartView.value === 'pie' && !isOutputDistribution.value) {
      chartView.value = 'bar'
    }

    isLoadingChart.value = true
    chartError.value = null

    try {
      const datetimeFrom = currentTZDateAndTimeToTimestamp(
        chartPeriod.value.startDate ?? state.value.filters.period?.startDate,
        chartPeriod.value.startTime ?? state.value.filters.period?.startTime,
      )
      const datetimeTo = currentTZDateAndTimeToTimestamp(
        chartPeriod.value.endDate ?? state.value.filters.period?.endDate,
        chartPeriod.value.endTime ?? state.value.filters.period?.endTime,
      )
      const { agents, allBut } = resolveAgentFilter(
        state.value.filters.agents,
        state.value.availableFilters.agents,
      )
      const languages = [...state.value.filters.languages]
      const disconnectedBy = [...state.value.filters.disconnectedBy]

      const response = await fetchColdChart(selectedKpi.value.id, {
        datetimeFrom,
        datetimeTo,
        chartInterval: chartView.value === 'bar' ? chartInterval.value : undefined,
        agents,
        allBut: allBut || undefined,
        languages,
        disconnectedBy,
        includeLiveTests: state.value.filters.showLiveTests,
      })

      const chart = response?.data[0]
      if (!chart) {
        clearChartData()
        return
      }

      chartData.value = {
        chartType: chart.chartType === 'stacked_bar' ? 'bar' : chart.chartType,
        series: chart.series.map((serie) => ({
          label: serie.label,
          data: serie.data,
        })),
        categories: chart.categories,
        colors: ['#6657EB', '#10B981', '#F59E0B', '#4F46E5'],
        loading: false,
      }
      rawCategories.value = chart.categories
    } catch (err) {
      chartError.value = err instanceof Error ? err.message : 'Unknown error'
      clearChartData()
    } finally {
      isLoadingChart.value = false
    }
  }

  const openModal = (kpi: ColdMonitoringKPI) => {
    const isOutputDistributionKpi = kpi.id === 'output-distribution'

    if (isOutputDistributionKpi) {
      chartView.value = outputDistributionView.value
    } else {
      chartView.value = 'bar'
    }

    localPeriod.value = clonePeriod(state.value.filters.period)
    selectedKpi.value = kpi

    clearChartData()

    chartError.value = null
    isLoadingChart.value = true
    isModalOpen.value = true
  }

  const closeModal = () => {
    isModalOpen.value = false
    selectedKpi.value = null
  }

  const setChartView = (view: 'bar' | 'pie') => {
    chartView.value = view
    if (isOutputDistribution.value) {
      outputDistributionView.value = view
    }
    void loadChart()
  }

  // ========== CHART COMPUTED PROPERTIES ==========

  const effectiveView = computed(() => (isOutputDistribution.value ? 'pie' : 'bar'))

  const formatAbsoluteChartLabel = (date: Date, interval: ChartInterval | '15m') =>
    formatDateTime({ format: CHART_FORMAT_BY_INTERVAL[interval], value: date, fallback: '' })

  const formattedCategories = computed(() => {
    if (!rawCategories.value) return rawCategories.value

    if (effectiveView.value === 'pie' || isOutputDistribution.value) {
      return rawCategories.value.map(normalizeChartCategoryLabel)
    }

    return formatChartCategories(rawCategories.value, chartInterval.value, {
      t,
      formatAbsoluteLabel: formatAbsoluteChartLabel,
    }).map(normalizeChartCategoryLabel)
  })

  const highlightBarIndex = computed(() =>
    effectiveView.value === 'bar'
      ? getCurrentHighlightIndex(rawCategories.value ?? [], chartInterval.value)
      : undefined,
  )

  const localizedSeries = computed(() => localizeChartUnits(chartData.value.series, t))

  const chartProps = computed<ChartProps>(() => {
    const view = effectiveView.value
    const series = localizedSeries.value

    if (view === 'pie' && isOutputDistribution.value) {
      const pieSeries =
        series.length && series[0]
          ? [
              {
                label: series[0].label,
                data: series[0].data,
              },
            ]
          : []

      return {
        ...chartData.value,
        chartType: 'pie',
        series: pieSeries,
        categories: rawCategories.value?.map(normalizeChartCategoryLabel),
        loading: isLoadingChart.value,
      }
    }

    const highlightIndex = highlightBarIndex.value
    const highlightMode: ChartProps['highlightBar'] =
      typeof highlightIndex === 'number' ? 'index' : 'none'

    return {
      ...chartData.value,
      loading: isLoadingChart.value,
      chartType: view === 'pie' ? 'pie' : 'bar',
      categories: formattedCategories.value,
      rawCategories: !isOutputDistribution.value ? rawCategories.value : undefined,
      series,
      highlightBar: highlightMode,
      highlightBarIndex: highlightIndex,
      ...(chartView.value === 'bar' &&
        !isOutputDistribution.value && {
          tooltipFormat: getChartTooltipFormat(chartInterval.value),
        }),
    }
  })

  const intervalOptions = computed(() =>
    AVAILABLE_INTERVALS.map((item) => ({
      label: capitalize(t('interval_by', [t(item)]).toLowerCase()),
      value: item,
    })),
  )

  const modalTitle = computed(() => {
    if (!selectedKpi.value) return 'volume_of_calls'
    if (selectedKpi.value.id === 'total-minutes')
      return 'cold_monitoring.kpi.total_calling_duration'
    return `cold_monitoring.kpi.${selectedKpi.value.title}`
  })
  const isOutputDistribution = computed(() => selectedKpi.value?.id === 'output-distribution')
  const showChartToggle = computed(() => false)
  const showChartIntervalDropdown = computed(() => {
    if (!isOutputDistribution.value) return true
    return chartView.value === 'bar'
  })

  // ========== WATCHERS ==========

  // Track if we should skip the next watcher trigger (for initial load)
  const skipNextRefresh = ref(false)
  const isInitialized = ref(false)

  // Single watcher for filters - watch the ref itself, not a copy
  watch(
    () => state.value.filters,
    async () => {
      // Don't trigger until we've completed initialization
      if (!isInitialized.value) {
        return
      }

      if (skipNextRefresh.value) {
        skipNextRefresh.value = false
        return
      }

      await refresh()
    },
    { deep: true, immediate: false },
  )

  // Separate watcher for charts (only when modal is open)
  watch(
    [chartPeriod, chartInterval, selectedKpi],
    () => {
      if (!selectedKpi.value) return
      void loadChart()
    },
    { deep: true },
  )

  // Sync localPeriod with filters.period
  watch(
    () => state.value.filters.period,
    (val) => {
      if (val?.startDate === '' && val.endDate === '') return
      localPeriod.value = clonePeriod(val)
    },
    { deep: true },
  )

  watch(
    () => [state.value.filters.period, state.value.filters.periodPresetId] as const,
    ([period, periodPresetId]) => {
      calendarRange.value = clonePeriod(period)
      calendarPresetId.value = periodPresetId
    },
    { deep: true, immediate: true },
  )

  // ========== COMPUTED EXPORTS ==========

  const filters = computed(() => state.value.filters)
  const pagination = computed(() => state.value.pagination)
  const sort = computed(() => state.value.sort)
  const availableFilters = computed(() => state.value.availableFilters)
  const status = computed(() => state.value.status)
  const callsList = computed(() => state.value.calls)
  const isDefaultFilters = computed(() => state.value.isDefaultFilters)
  const hasUserInteracted = computed(() => state.value.hasUserInteracted)

  // ========== INITIALIZATION ==========

  onMounted(async () => {
    loadPreferences()
    // Skip the watcher trigger that will occur due to loadPreferences() modifying filters
    skipNextRefresh.value = true
    isInitialized.value = true
    await refresh()
  })

  return {
    // State
    filters,
    pagination,
    sort,
    availableFilters,
    status,

    // Stats
    stats,
    isLoadingStats,
    statsError,

    // Filter tracking
    isDefaultFilters,
    hasUserInteracted,

    // Computed helpers
    callsList,
    calendarRange,
    calendarPresetId,

    // Actions
    applyFilters,
    setPeriodFilter,
    setCalendarRange,
    setCalendarPresetId,
    applyCalendarFilter,
    clearCalendarFilter,
    setPage,
    setPageSize,
    setSort,
    resetFilters,
    refresh,

    // Charts
    isModalOpen,
    chartProps,
    chartInterval,
    chartView,
    setChartView,
    showChartIntervalDropdown,
    chartPeriod,
    intervalOptions,
    modalTitle,
    openModal,
    closeModal,
    showChartToggle,
    chartError,
  }
}
